import React from 'react';
import { BarChart, Trash2, Layers } from 'lucide-react';
import { formatCurrency, formatNumber } from '../../../../../utils/formatting';
import Button from '../../../../../components/ui/Button';

/**
 * Компонент боковой панели для сравнения сохраненных сценариев
 */
const ScenarioComparisonSidebar = ({ 
  savedScenarios, 
  deleteSavedScenario, 
  loadSavedScenario 
}) => {
  // Если нет сохраненных сценариев
  if (!savedScenarios || savedScenarios.length === 0) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 shadow-sm">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4 flex items-center">
          <BarChart className="w-5 h-5 mr-2 text-indigo-500" />
          Сравнение сценариев
        </h3>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Здесь будут отображаться сохраненные сценарии финансовой независимости для сравнения.
        </p>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 shadow-sm">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white flex items-center">
          <BarChart className="w-5 h-5 mr-2 text-indigo-500" />
          Сравнение сценариев
        </h3>
        <span className="text-sm text-gray-500 dark:text-gray-400">
          {savedScenarios.length} {pluralize(savedScenarios.length, 'сценарий', 'сценария', 'сценариев')}
        </span>
      </div>

      <div className="space-y-4">
        {savedScenarios.map((scenario) => (
          <div 
            key={scenario.id} 
            className="bg-gray-50 dark:bg-gray-700/30 border border-gray-200 dark:border-gray-700 rounded-lg p-4 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors group"
          >
            <div className="flex justify-between items-start mb-2">
              <h4 className="text-sm font-medium text-gray-900 dark:text-white">
                {scenario.name}
              </h4>
              <div className="flex items-center space-x-2">
                <Button
                  size="sm"
                  variant="ghost"
                  icon={<Layers className="w-4 h-4" />}
                  onClick={() => loadSavedScenario(scenario.id)}
                  title="Загрузить сценарий"
                />
                <Button
                  size="sm"
                  variant="ghost"
                  icon={<Trash2 className="w-4 h-4 text-red-500" />}
                  onClick={() => deleteSavedScenario(scenario.id)}
                  title="Удалить"
                />
              </div>
            </div>
            <div className="text-sm text-gray-600 dark:text-gray-400">
              <div className="flex justify-between mb-1">
                <span>Стратегия:</span>
                <span className="font-medium">
                  {getStrategyName(scenario.formData.investmentStrategy)}
                </span>
              </div>
              <div className="flex justify-between mb-1">
                <span>Доходность:</span>
                <span>
                  {formatNumber(scenario.formData.investmentReturn, 1)}%
                </span>
              </div>
              <div className="flex justify-between mb-1">
                <span>Выход на пенсию:</span>
                <span>
                  {scenario.formData.retirementAge} лет
                </span>
              </div>
              <div className="flex justify-between mb-1">
                <span>Целевой капитал:</span>
                <span className="font-medium text-indigo-600 dark:text-indigo-400">
                  {formatCurrency(scenario.results.targetCapital)}
                </span>
              </div>
              <div className="flex justify-between">
                <span>Пассивный доход:</span>
                <span className="font-semibold text-green-600 dark:text-green-400">
                  {formatCurrency(scenario.results.projectedPassiveIncomeAtRetirement)}/мес
                </span>
              </div>
              <div className="mt-2 pt-2 border-t border-gray-200 dark:border-gray-600">
                <span className={`text-xs font-medium rounded-full px-2 py-0.5 ${
                  scenario.results.financialIndependenceAchieved
                    ? 'bg-green-100 text-green-600 dark:bg-green-900/30 dark:text-green-400'
                    : 'bg-orange-100 text-orange-600 dark:bg-orange-900/30 dark:text-orange-400'
                }`}>
                  {scenario.results.financialIndependenceAchieved
                    ? `Успех в ${scenario.results.financialIndependenceAge} лет`
                    : 'Цель не достигнута'}
                </span>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// Функция для получения названия стратегии
const getStrategyName = (strategyId) => {
  const strategies = {
    'conservative': 'Консервативная',
    'balanced': 'Сбалансированная',
    'aggressive': 'Агрессивная',
    'custom': 'Собственная'
  };
  
  return strategies[strategyId] || 'Неизвестная';
};

// Вспомогательная функция для склонения слов
const pluralize = (number, one, two, five) => {
  let n = Math.abs(number);
  n %= 100;
  if (n >= 5 && n <= 20) {
    return five;
  }
  n %= 10;
  if (n === 1) {
    return one;
  }
  if (n >= 2 && n <= 4) {
    return two;
  }
  return five;
};

export default ScenarioComparisonSidebar;